import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useErrorSnackbar } from "../../../hooks/useErrorSnackbar";
import { backendService } from "../../../services/backend.service";
import type { HeldCellValue } from "../hooks";
import type { AddToDepartmentItem, AddToDepartmentResponse, CreateHeldResponse } from "./heldRowModel";

const errorDetail = (error: Error): string => {
	const detail = (error as AxiosError<{ detail?: unknown }>).response?.data?.detail;
	return typeof detail === "string" ? detail : error.message;
};

type AddParams = { departmentId: string; items: AddToDepartmentItem[]; bulk: boolean };

/** Held roster rows → add this facility to each chosen existing record. */
export function useAddHeldToDepartment() {
	const { t } = useTranslation();
	const showErrorSnackbar = useErrorSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async (params: AddParams): Promise<AddToDepartmentResponse> =>
			(await backendService.addHeldRowsToDepartment({ departmentId: params.departmentId, items: params.items })).data,
		onMutate: (params) => track("onboarding_held_add_start", { department_id: params.departmentId, items: params.items.length, bulk: params.bulk }),
		onSuccess: (response, params) =>
			track("onboarding_held_add_success", {
				department_id: params.departmentId,
				added: response.results.filter((result) => result.status === "added").length,
				not_added: response.results.filter((result) => result.status !== "added").length,
			}),
		onError: (error, params) => {
			trackError("onboarding_held_add_error", { department_id: params.departmentId, error_message: errorDetail(error) });
			showErrorSnackbar(t("onboarding_load.held.add_failed"), { department_id: params.departmentId }, error);
		},
	});
}

type CreateParams = { departmentId: string; isUpdate: boolean; rows: Array<Record<string, HeldCellValue>> };

/** Held roster rows → create them as separate records after all. */
export function useCreateHeldRows() {
	const { t } = useTranslation();
	const showErrorSnackbar = useErrorSnackbar();
	const { track, trackError } = useAnalytics();

	return useMutation({
		mutationFn: async (params: CreateParams): Promise<CreateHeldResponse> => (await backendService.createHeldRows(params)).data,
		onMutate: (params) => track("onboarding_held_create_start", { department_id: params.departmentId, rows: params.rows.length }),
		onSuccess: (response, params) =>
			track("onboarding_held_create_success", { department_id: params.departmentId, created: response.summary.success, failed: response.summary.failed }),
		onError: (error, params) => {
			trackError("onboarding_held_create_error", { department_id: params.departmentId, error_message: errorDetail(error) });
			showErrorSnackbar(t("onboarding_load.held.create_failed"), { department_id: params.departmentId }, error);
		},
	});
}
